import './IconButton.css';
import type { JSX, Ref } from 'preact';
import { Icon } from './Icon';
import type { IconName } from './icons';

export type IconButtonVariant = 'plain' | 'bordered';
/** Caixa no ponteiro fino: 30 (padrão, §3.8), 28 (redes sociais §6.2, DevPanel) ou 24 (carrossel §7.14). */
export type IconButtonBox = 30 | 28 | 24;

type NativeAttrs = Omit<
  JSX.HTMLAttributes<HTMLButtonElement>,
  | 'class'
  | 'className'
  | 'children'
  | 'type'
  | 'disabled'
  | 'onClick'
  | 'ref'
  | 'aria-label'
  | 'aria-disabled'
  | 'title'
  | 'icon'
  | 'label'
>;

export interface IconButtonProps extends NativeAttrs {
  icon: IconName;
  /** Nome acessível (`aria-label`), obrigatório e não vazio (AC-3.8-09). */
  label: string;
  /** Dica nativa; igual ao rótulo quando houver atalho ("Aproximar (+)"). Nunca substitui o `label`. */
  title?: string;
  disabled?: boolean;
  /** Desabilitado mas ainda focável (`aria-disabled`, ex.: ‹ na primeira página do `Pager`). */
  focusableWhenDisabled?: boolean;
  variant?: IconButtonVariant;
  box?: IconButtonBox;
  /** Ícone de 14 a 16 px (§3.8). */
  iconSize?: 14 | 15 | 16;
  onClick?: (event: MouseEvent) => void;
  /** Classes extras do dono (ex.: `tv-hide` nos de navegação). */
  class?: string;
  buttonRef?: Ref<HTMLButtonElement>;
}

/** Botão só com ícone (§3.8): redondo, nome acessível sempre pelo `aria-label`. */
export function IconButton({
  icon,
  label,
  title,
  disabled = false,
  focusableWhenDisabled = false,
  variant = 'plain',
  box = 30,
  iconSize = 16,
  onClick,
  class: extra,
  buttonRef,
  ...rest
}: IconButtonProps) {
  if (import.meta.env.DEV && label.trim() === '') {
    throw new Error(`[IconButton] "${icon}" sem label: o aria-label é obrigatório (AC-3.8-09)`);
  }
  const softDisabled = disabled && focusableWhenDisabled;
  const cls = `icon-button icon-button--${variant} icon-button--${box}${extra ? ` ${extra}` : ''}`;
  return (
    <button
      {...rest}
      ref={buttonRef}
      type="button"
      class={cls}
      data-testid="icon-button"
      aria-label={label}
      title={title}
      disabled={disabled && !softDisabled}
      aria-disabled={softDisabled ? 'true' : undefined}
      onClick={disabled ? undefined : onClick}
    >
      <Icon name={icon} size={iconSize} />
    </button>
  );
}
